import { z } from 'zod';
import { UUIDSchema } from '../domain/models';
import { PaginatedBundleSchema } from '../domain/schema';

const PriorityScoringSchema = z.object({
  value: z.number(),
});

const QuestionTypeSchema = z.enum([
  'string',
  'boolean',
  'reference',
  'display',
  'integer',
  'date',
  'text',
  'choice',
  'group',
  'question',
  'decimal',
  'dateTime',
  'time',
  'url',
  'open-choice',
  'attachment',
  'quantity',
]);

const OperatorSchema = z.enum(['=', '>', '<', '>=', '<=', '!=', 'exists']);

const AnswerOptionSchema = z.object({
  value: z.union([z.string(), z.number(), z.boolean(), z.date()]),
  isDefaultAnswer: z.boolean(),
  priorityScoring: PriorityScoringSchema.optional(),
});

const ConditionSchema = z.object({
  relatedLinkId: z.string(),
  operator: OperatorSchema,
  value: z.union([z.string(), z.boolean(), z.number()]),
});

const QuestionSchema: z.ZodSchema = z.lazy(() =>
  z.object({
    linkId: z.string(),
    text: z.string().optional(),
    type: QuestionTypeSchema,
    required: z.boolean().optional(),
    answerOptions: AnswerOptionSchema.array().min(1),
    questions: z.array(QuestionSchema).optional(),
    condition: ConditionSchema.optional(),
  }),
);

const QuestionnaireSectionSchema = z.object({
  type: QuestionTypeSchema,
  icon: z.string().optional(),
  linkId: z.string(),
  required: z.boolean().optional(),
  answerOptions: AnswerOptionSchema.array(),
  questions: z.array(QuestionSchema).optional(),
  condition: ConditionSchema.optional(),
  text: z.string().optional(),
});

export const QuestionnaireSchema = z.object({
  id: UUIDSchema,
  url: z.string().optional(),
  title: z.string(),
  icon: z.string().optional(),
  section: QuestionnaireSectionSchema.array().min(1),
});

export const QuestionnairesSearchParamsSchema = z.object({
  carePlanId: UUIDSchema,
});

export const QuestionnaireSearchParamsSchema = z.object({
  questionnaire: UUIDSchema,
});

export const QuestionnaireGetAllResponseSchema = PaginatedBundleSchema.extend({
  questionnaires: QuestionnaireSchema.array(),
});

export type QuestionnaireSection = z.infer<typeof QuestionnaireSectionSchema>;
export type Questionnaire = z.infer<typeof QuestionnaireSchema>;
export type Question = z.infer<typeof QuestionSchema>;
export type QuestionnaireSearchParams = z.infer<
  typeof QuestionnaireSearchParamsSchema
>;
export type QuestionnaireGetAllResponse = z.infer<
  typeof QuestionnaireGetAllResponseSchema
>;
export type AnswerOption = z.infer<typeof AnswerOptionSchema>;
export type QuestionType = z.infer<typeof QuestionTypeSchema>;
export type Condition = z.infer<typeof ConditionSchema>;
